import { purchaseOrderReference } from '@sitzmark/core';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Barcode } from '../../components/barcode.js';
import { api } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import { DocumentGrid } from './document-grid-view.js';
import './receiving-document.css';

/**
 * The purchase order, on paper (SCOPE §6.1).
 *
 * The client asked to print or email an order, and the product could do
 * neither: an order existed only as rows in a table nobody outside the shop
 * could see. A vendor who takes orders by fax, by post, or by "send me a PDF"
 * had no path at all, and a buyer who wanted a copy in the season folder had to
 * screenshot a screen.
 *
 * **Letter paper, and it reuses the receiving document's stylesheet.** The two
 * are the same kind of object — a page that goes in a folder and gets read
 * against another piece of paper — so they share `@page`, type scale and table
 * rules rather than growing a second set that drifts. The one difference is
 * what they assert: this says what was *asked for*, at the cost that was
 * agreed; the receiving document says what turned up.
 */

const PAGE_RULE = '@page { size: letter; margin: 0.6in; }';

type Document = Awaited<ReturnType<typeof api.purchasing.purchaseOrderDocument.query>>;

export function usePurchaseOrderDocument() {
  const [printing, setPrinting] = useState<Document | null>(null);

  const print = useCallback((document: Document) => {
    const style = window.document.createElement('style');
    style.textContent = PAGE_RULE;
    window.document.head.appendChild(style);

    try {
      // Synchronously, before `window.print()` — React would otherwise batch
      // the update until after the dialog had captured a blank page.
      flushSync(() => setPrinting(document));
      window.print();
    } finally {
      setPrinting(null);
      style.remove();
    }
  }, []);

  const frame =
    printing === null
      ? null
      : createPortal(
          <div className="print-only">
            <PurchaseOrderDocument document={printing} />
          </div>,
          window.document.body,
        );

  return { print, frame };
}

/**
 * Load the order and print it, which is the whole interaction.
 *
 * Fetched on demand rather than alongside the list: printing an order is what
 * happens once, when it is placed, and the purchase-order list is on the path
 * of a delivery.
 */
export function usePurchaseOrderPrinter() {
  const printer = usePurchaseOrderDocument();
  /*
   * An action, not a cached query (2026-10-02): the print used to run inside a
   * query's loader, and the app keeps a query fresh for fifteen seconds — so a
   * second press on the same order, a cancelled dialog and a quick retry,
   * found the cached copy, ran no loader, and printed nothing. Every press now
   * loads and prints.
   */
  const load = useMutation({
    mutationFn: (purchaseOrderId: string) =>
      api.purchasing.purchaseOrderDocument.query({ purchaseOrderId }),
    onSuccess: (loaded) => printer.print(loaded),
  });

  return {
    frame: printer.frame,
    busy: load.isPending,
    error: load.error,
    print: (purchaseOrderId: string) => load.mutate(purchaseOrderId),
  };
}

export function PurchaseOrderDocument({ document }: { document: Document }) {
  const header = document.purchaseOrder;
  const store = document.store;
  const reference = purchaseOrderReference(header.vendorReference);

  return (
    <article className="rodoc">
      <header className="rodoc__head">
        <div>
          <div className="rodoc__store">{store.name}</div>
          {store.addressLine1 == null ? null : <div>{store.addressLine1}</div>}
          {store.city == null ? null : (
            <div>
              {store.city}
              {store.region == null ? '' : `, ${store.region}`} {store.postalCode ?? ''}
            </div>
          )}
          {store.phone == null ? null : <div>{store.phone}</div>}
          {/* Where the vendor writes back (FB-1067). */}
          {store.email == null ? null : <div>{store.email}</div>}
        </div>
        <div className="rodoc__title">
          <h1>Purchase order</h1>
          <div className="rodoc__number">{header.number}</div>
          {/* Scanned into Purchase orders, it opens this order (2026-10-02). */}
          <Barcode value={header.number} className="rodoc__barcode" />
        </div>
      </header>

      <dl className="rodoc__meta">
        <div>
          <dt>Vendor</dt>
          {/* The whole block (item 25): who to call, and where the goods come
              from — top of the paper, next to the shop's own address. */}
          <dd>
            {header.vendorName}
            {header.vendorContactName == null ? '' : ` · attn ${header.vendorContactName}`}
            {header.vendorAddressLine1 == null ? null : (
              <>
                <br />
                {header.vendorAddressLine1}
              </>
            )}
            {header.vendorAddressLine2 == null ? null : (
              <>
                <br />
                {header.vendorAddressLine2}
              </>
            )}
            {header.vendorCity == null && header.vendorRegion == null ? null : (
              <>
                <br />
                {[header.vendorCity, header.vendorRegion].filter(Boolean).join(', ')}{' '}
                {header.vendorPostalCode ?? ''}
              </>
            )}
            {header.vendorPhone == null ? null : (
              <>
                <br />
                {header.vendorPhone}
              </>
            )}
            {header.vendorEmail == null ? null : (
              <>
                <br />
                {header.vendorEmail}
              </>
            )}
          </dd>
        </div>
        {header.vendorAccountNumber == null ? null : (
          <div>
            <dt>Account</dt>
            <dd>{header.vendorAccountNumber}</dd>
          </div>
        )}
        {/* The vendor's number, or a Rain order's old one named as ours (FB-1130). */}
        {reference === null ? null : (
          <div>
            <dt>{reference.label}</dt>
            <dd>{reference.value}</dd>
          </div>
        )}
        {header.season == null ? null : (
          <div>
            <dt>Season</dt>
            <dd>{header.season}</dd>
          </div>
        )}
        <div>
          <dt>Ordered</dt>
          {/* Named rather than blank: an order that has not been sent is a real
              state, and an empty date reads as missing data (§2.2-10). */}
          <dd>{header.orderedAt ?? 'Not yet sent'}</dd>
        </div>
        {header.shipDate == null ? null : (
          <div>
            <dt>Ship by</dt>
            <dd>{header.shipDate}</dd>
          </div>
        )}
        {header.cancelDate == null ? null : (
          <div>
            <dt>Cancel after</dt>
            <dd>{header.cancelDate}</dd>
          </div>
        )}
        {/* FB-1066: the terms and any discount, as the vendor reads them. */}
        {header.terms == null ? null : (
          <div>
            <dt>Terms</dt>
            <dd>{header.terms}</dd>
          </div>
        )}
        {header.discountTerms == null ? null : (
          <div>
            <dt>Discount</dt>
            <dd>{header.discountTerms}</dd>
          </div>
        )}
      </dl>

      {/* The grid (item 20): one block per style, colours down, sizes across
          — the format every vendor order form uses. The style number in each
          block heading is the vendor's own reference. */}
      <DocumentGrid lines={document.lines} />

      <p className="rodoc__notes">
        {document.totals.lines} line{document.totals.lines === 1 ? '' : 's'} ·{' '}
        {document.totals.units} units · {money(document.totals.costCents)} at cost
      </p>

      {header.notes == null ? null : <p className="rodoc__notes">{header.notes}</p>}

      <div className="rodoc__sign">
        <div>
          <span className="rodoc__rule" />
          Authorized by
        </div>
        <div>
          <span className="rodoc__rule" />
          Date
        </div>
      </div>
    </article>
  );
}
